import React, { FC, useContext, useEffect, useMemo, useState } from 'react';
import { Formik, getIn } from 'formik';
import { format } from 'date-fns';
import * as Yup from 'yup';
import { useHistory } from 'react-router-dom';
import {
  Alert,
  Asset,
  Button,
  Checkbox,
  HelpIcon,
  Tooltip,
} from '@clearcover/atlas';

import {
  AnimatedScreen,
  Column,
  Footer,
  FormInputErrorMessage,
  HRule,
  Modal,
  ModalPromptContent,
  Row,
  TableProps,
} from 'Shared/components';
import { useLazyBaseApi } from 'Shared/services/Api';
import { PartnerContext } from 'Admin/utils';
import {
  PartnerTypeaheadResponse,
  UserTypeaheadResponse,
  getFetchPartnersConfig,
  getFetchUsersConfig,
} from 'Admin/services/endpoints/PostAttributionTypeahead';
import { DatePickerModal, formatDatePickerDate } from 'QuoteApp/components'; // :( we have two date picker modal components; the "Shared" one is heavily policy specific so here we are
import {
  AgencyDetails,
  AgentDetails,
  buildPolicyListRow,
  DirectionalCard,
  DirectionalCardContainer,
  PoliciesTable,
  Typeahead,
  TypeaheadResult,
} from '../../components';
import { useReattributionRedirect } from '../../hooks';
import { addSelection, removeSelection, useStore } from '../../store';

import styles from '../styles.module.css'; // Shared styles from reattribution screens
import confirmationStyles from './styles.module.css'; // confirmation specific styles

type ReattributionFormType = Record<
  string,
  {
    agency: {
      displayValue?: string;
      selectedId?: string;
    };
    agent: {
      displayValue?: string;
      selectedId?: string;
    };
    hasAgentOfRecord: boolean;
    effectiveDate: string;
    documentationCollected: boolean;
    selectedIds: Array<string>;
  }
>;

export const Confirmation: FC = () => {
  const { currentPartner } = useContext(PartnerContext);
  const history = useHistory();
  const [state, dispatch] = useStore();
  const [initialPartnerRequestLoading, setInitialPartnerRequestLoading] =
    useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);

  const canViewRoute = useReattributionRedirect();

  const [{ loading: loadingUsers }, fetchUsers] =
    useLazyBaseApi<UserTypeaheadResponse>({});

  const [{ loading: loadingPartners }, fetchPartners] =
    useLazyBaseApi<PartnerTypeaheadResponse>({});

  const [{ error }, reattributePolicies] = useLazyBaseApi({
    method: 'POST',
    url: '/policies/attribution/reattribute',
  });

  const prefix = useMemo(() => new Date().getTime().toString(), []);
  const initialValues = useMemo(() => {
    return {
      // to prevent browser autofill on typeaheads
      [prefix]: {
        agency: {
          displayValue: '',
          selectedId: '',
        },
        agent: {
          displayValue: '',
          selectedId: '',
        },
        hasAgentOfRecord: true,
        effectiveDate: formatDatePickerDate(new Date()),
        documentationCollected: false,
        selectedIds: state.policies.selections || [],
      },
    };
  }, [prefix, state.policies.selections]);

  const validations = Yup.object().shape({
    [prefix]: Yup.object().shape({
      agency: Yup.object().shape({
        selectedId: Yup.string().required('Agency is required'),
      }),
      hasAgentOfRecord: Yup.bool(),
      agent: Yup.object().when('hasAgentOfRecord', {
        is: false,
        then: Yup.object().shape({
          selectedId: Yup.string().nullable(),
        }),
        otherwise: Yup.object().shape({
          selectedId: Yup.string().required('Agent is required'),
        }),
      }),
      effectiveDate: Yup.string().required(
        'Reattribution effective date is required'
      ),
      documentationCollected: Yup.bool().oneOf(
        [true],
        'Confirmation is required'
      ),
      selectedIds: Yup.array().min(1, 'At least one policy must be selected.'),
    }),
  });

  const getFullFieldName = (fieldName: string) => `${prefix}.${fieldName}`;

  useEffect(() => {
    const getCurrentPartnerDetails = async () => {
      const config = getFetchPartnersConfig(currentPartner!.name);
      try {
        setInitialPartnerRequestLoading(true);
        const response = await fetchPartners(config);
        const currentPartnerDetails = response.data.find(
          (agency) => agency.id === currentPartner!.id
        );
        if (currentPartnerDetails) {
          dispatch({ type: 'from.agency.set', value: currentPartnerDetails });
        } else {
          throw new Error('An error occurred');
        }
      } catch (e) {
        console.error(e);
      } finally {
        setInitialPartnerRequestLoading(false);
      }
    };

    if (
      currentPartner &&
      !loadingPartners &&
      !state.from.agency &&
      !initialPartnerRequestLoading &&
      canViewRoute
    ) {
      getCurrentPartnerDetails();
    }
  }, [
    currentPartner,
    dispatch,
    fetchPartners,
    initialPartnerRequestLoading,
    loadingPartners,
    state.from.agency,
    canViewRoute,
  ]);

  useEffect(() => {
    return () => {
      // reset agency and agent on unmount
      dispatch({
        type: 'to.agency.set',
        value: null,
      });
      dispatch({
        type: 'to.agent.set',
        value: null,
      });
    };
  }, [dispatch]);

  if (!currentPartner || initialPartnerRequestLoading || !canViewRoute)
    return null;

  return (
    <AnimatedScreen testId="partner-reattribution-confirmation-page">
      <h1>Policy Reattribution</h1>
      <HRule size="lg" />
      <Formik<ReattributionFormType>
        initialValues={initialValues}
        validationSchema={validations}
        validateOnMount
        onSubmit={async (values) => {
          try {
            const sanitizedDate = format(
              new Date(values[prefix].effectiveDate),
              'yyyy-MM-dd'
            );
            await reattributePolicies({
              data: {
                destinationPartnerId: values[prefix].agency.selectedId,
                destinationPortalUserId: values[prefix].hasAgentOfRecord
                  ? values[prefix].agent.selectedId
                  : null,
                hasAgentOfRecord: values[prefix].hasAgentOfRecord,
                universalPolicyIds: values[prefix].selectedIds,
                effectiveDate: sanitizedDate,
                sourcePartnerId: state.from?.agency?.id,
              },
            });

            dispatch({
              type: 'reattribution.success',
              effectiveDate: values[prefix].effectiveDate,
              selectedPolicies: values[prefix].selectedIds,
            });

            history.push(
              `/admin/partners/${currentPartner.id}/reattribution/success`
            );
          } catch {
            // Error from hook is displayed in footer
            setShowModal(false);
          }
        }}
      >
        {({
          setFieldValue,
          setValues,
          values,
          handleSubmit,
          errors,
          touched,
          isSubmitting,
          isValid,
        }) => {
          const getErrorMessage = (
            touchedFieldName: string,
            errorFieldName?: string
          ) =>
            getIn(touched, getFullFieldName(touchedFieldName))
              ? getIn(
                  errors,
                  getFullFieldName(errorFieldName || touchedFieldName)
                )
              : undefined;

          return (
            <>
              <Modal showModal={showModal} onClose={() => setShowModal(false)}>
                <ModalPromptContent
                  confirmButtonText="Submit Reattribution"
                  cancelButtonText="Cancel"
                  onCancel={() => setShowModal(false)}
                  onConfirm={() => handleSubmit()}
                  isSubmitting={isSubmitting}
                >
                  <h3>Submit Reattribution?</h3>
                  <p>
                    {`This change will migrate ${
                      values[prefix].selectedIds.length
                    } ${
                      values[prefix].selectedIds.length === 1
                        ? 'policy'
                        : 'policies'
                    } from ${state.from.agency?.name} to ${
                      state.to.agency?.name
                    } effective on ${
                      values[prefix].effectiveDate
                    }. This change can't be easily reverted.`}
                  </p>
                </ModalPromptContent>
              </Modal>
              <DirectionalCardContainer layout="vertical">
                <DirectionalCard>
                  <div className={styles.cardHeading}>Policies From:</div>
                  <HRule />
                  <Row>
                    <Column variant="half">
                      <AgencyDetails agency={state.from.agency} />
                    </Column>
                    <Column variant="half">
                      <AgentDetails agent={state.from.agent} />
                    </Column>
                  </Row>
                </DirectionalCard>
                <DirectionalCard>
                  <div className={styles.cardHeading}>Policies To:</div>
                  <HRule />
                  <Row className={confirmationStyles.alignTop}>
                    <Column variant="half">
                      <Typeahead<PartnerTypeaheadResponse>
                        data-testid="partner-typeahead"
                        name={`${prefix}.agency.displayValue`}
                        label="Search Agency"
                        debounce={200}
                        value={values[prefix].agency.displayValue}
                        setValue={(value) => {
                          setFieldValue(
                            getFullFieldName('agency.displayValue'),
                            value
                          );
                        }}
                        errorMessage={getErrorMessage(
                          'agency.displayValue',
                          'agency.selectedId'
                        )}
                        loading={loadingPartners}
                        lazyRequest={fetchPartners}
                        getLazyRequestConfig={getFetchPartnersConfig}
                        isClearable={!!state.to.agency}
                        disabled={isSubmitting}
                        onClear={() => {
                          dispatch({
                            type: 'to.agency.set',
                            value: null,
                          });
                          dispatch({
                            type: 'to.agent.set',
                            value: null,
                          });
                          setValues({
                            [prefix]: {
                              ...values[prefix],
                              agency: {
                                ...initialValues[prefix].agency,
                              },
                              agent: {
                                ...initialValues[prefix].agent,
                              },
                            },
                          });
                        }}
                        onBlur={() => {
                          if (
                            state.to.agency &&
                            state.to.agency.name !==
                              values[prefix].agency.displayValue
                          ) {
                            setFieldValue(
                              getFullFieldName('agency.displayValue'),
                              state.to.agency.name
                            );
                          }
                        }}
                        onSelect={(selectedId, options) => {
                          const selectedOption = options.find(
                            (option) => option.id === selectedId
                          );

                          if (selectedOption) {
                            dispatch({
                              type: 'to.agency.set',
                              value: selectedOption,
                            });
                            dispatch({
                              type: 'to.agent.set',
                              value: null,
                            });
                            setValues({
                              [prefix]: {
                                ...values[prefix],
                                agency: {
                                  displayValue: selectedOption.name,
                                  selectedId: selectedOption.id,
                                },
                                agent: {
                                  ...initialValues[prefix].agent,
                                },
                              },
                            });
                          }
                        }}
                        transformOptions={(options: PartnerTypeaheadResponse) =>
                          options.reduce((acc, current) => {
                            return {
                              ...acc,
                              [current.id]: (
                                <TypeaheadResult primaryResult={current.name} />
                              ),
                            };
                          }, {})
                        }
                      />
                      <AgencyDetails agency={state.to.agency} />
                    </Column>
                    <Column variant="half">
                      {state.to.agency && (
                        <>
                          <Typeahead<UserTypeaheadResponse>
                            data-testid="user-typeahead"
                            name={`${prefix}.agent.displayValue`}
                            label="Search Agent"
                            debounce={200}
                            value={
                              values[prefix].hasAgentOfRecord
                                ? values[prefix].agent.displayValue
                                : 'No Agent of Record'
                            }
                            setValue={(value) => {
                              setFieldValue(
                                getFullFieldName('agent.displayValue'),
                                value
                              );
                            }}
                            errorMessage={
                              values[prefix].hasAgentOfRecord
                                ? getErrorMessage(
                                    'agent.displayValue',
                                    'agent.selectedId'
                                  )
                                : undefined
                            }
                            loading={loadingUsers}
                            disabled={
                              !values[prefix].hasAgentOfRecord ||
                              !state.to.agency ||
                              isSubmitting
                            }
                            lazyRequest={fetchUsers}
                            getLazyRequestConfig={(value) =>
                              getFetchUsersConfig(
                                value,
                                {
                                  url: `/partners/${state.to.agency?.id}/portal_users`,
                                },
                                { active: true, hasNPN: true }
                              )
                            }
                            isClearable={
                              values[prefix].hasAgentOfRecord &&
                              !!state.to.agent
                            }
                            onClear={() => {
                              setValues({
                                [prefix]: {
                                  ...values[prefix],
                                  agent: { ...initialValues[prefix].agent },
                                },
                              });
                              dispatch({ type: 'to.agent.set', value: null });
                            }}
                            onBlur={() => {
                              if (
                                state.to.agent &&
                                state.to.agent.name !==
                                  values[prefix].agent.displayValue
                              ) {
                                setFieldValue(
                                  getFullFieldName('agent.displayValue'),
                                  state.to.agent.name
                                );
                              }
                            }}
                            onSelect={(selectedId, options) => {
                              const selectedOption = options.find(
                                (option) => option.id === selectedId
                              );
                              if (selectedOption) {
                                dispatch({
                                  type: 'to.agent.set',
                                  value: selectedOption,
                                });
                                setValues({
                                  [prefix]: {
                                    ...values[prefix],
                                    agent: {
                                      displayValue: selectedOption.name,
                                      selectedId: selectedOption.id,
                                    },
                                  },
                                });
                              }
                            }}
                            transformOptions={(
                              options: UserTypeaheadResponse
                            ) =>
                              options.reduce((acc, current) => {
                                return {
                                  ...acc,
                                  [current.id]: (
                                    <TypeaheadResult
                                      primaryResult={current.name}
                                      secondaryResult={current.email}
                                    />
                                  ),
                                };
                              }, {})
                            }
                          />
                          {values[prefix].hasAgentOfRecord && (
                            <AgentDetails agent={state.to.agent} />
                          )}
                          <Checkbox
                            data-testid="has-agent-of-record-checkbox"
                            name={getFullFieldName('hasAgentOfRecord')}
                            label="Has agent of record"
                            value={values[prefix].hasAgentOfRecord}
                            onChange={(checked) => {
                              setValues({
                                [prefix]: {
                                  ...values[prefix],
                                  hasAgentOfRecord: checked,
                                  agent: !checked
                                    ? { ...initialValues[prefix].agent }
                                    : values[prefix].agent,
                                },
                              });
                              if (!checked) {
                                dispatch({ type: 'to.agent.set', value: null });
                              }
                            }}
                          />
                        </>
                      )}
                    </Column>
                  </Row>
                  <Row>
                    <Column variant="half">
                      <DatePickerModal
                        modalHeader="Reattribution Effective Date"
                        label="Reattribution effective date"
                        maxDateAllowed={new Date()}
                        startDate={new Date(values[prefix].effectiveDate)}
                        onDateChange={(date) => {
                          setFieldValue(
                            getFullFieldName('effectiveDate'),
                            formatDatePickerDate(date)
                          );
                        }}
                        tooltip="Reattribution effective date can be in the past or today."
                        tooltipPosition="top"
                        errorMessage={getErrorMessage('effectiveDate')}
                        disabled={isSubmitting}
                      />
                    </Column>
                    <Column />
                  </Row>
                </DirectionalCard>
              </DirectionalCardContainer>
              <div className={confirmationStyles.documentationCollected}>
                <Checkbox
                  data-testid="documentation-collected-checkbox"
                  className={confirmationStyles.checkbox}
                  name={`${prefix}.documentationCollected`}
                  label="I have confirmed that documentation has been collected by Clearcover for this reattribution."
                  value={values[prefix].documentationCollected}
                  onChange={(checked) => {
                    setFieldValue(
                      getFullFieldName('documentationCollected'),
                      checked
                    );
                  }}
                  error={!!getErrorMessage('documentationCollected')}
                />
                <Tooltip
                  tooltip="Clearcover needs to be able to verify this reattribution with proper documentation. Documentation might include an ACORD Agent of Record form, bill of sale, agency sale agreement, or sale contract."
                  position="top"
                >
                  <Asset
                    icon={HelpIcon}
                    description="tooltip"
                    height={16}
                    width={16}
                    className={confirmationStyles.helpIcon}
                  />
                </Tooltip>
              </div>
              {!!getErrorMessage('documentationCollected') && (
                <div className={confirmationStyles.errorContainer}>
                  <FormInputErrorMessage
                    message={getErrorMessage('documentationCollected')}
                  />
                </div>
              )}
              <HRule size="md" />
              <h4>Review Policies for Reattribution</h4>
              <PoliciesTable
                name="reattribution-confirmation-table"
                rows={state.policies.data.reduce(
                  (acc: TableProps['rows'], row) => {
                    const selectedOnListScreen =
                      state.policies.selections.includes(row.universalPolicyId);

                    const selectedOnConfirmScreen = values[
                      prefix
                    ].selectedIds.includes(row.universalPolicyId);

                    if (selectedOnListScreen) {
                      acc.push(
                        buildPolicyListRow(row, selectedOnConfirmScreen)
                      );
                    }
                    return acc;
                  },
                  []
                )}
                onCheckboxSelect={(key, selected) => {
                  const selections = selected
                    ? addSelection(key.toString(), values[prefix].selectedIds)
                    : removeSelection(
                        key.toString(),
                        values[prefix].selectedIds
                      );
                  setFieldValue(getFullFieldName('selectedIds'), selections);
                }}
                onCheckboxSelectAll={(selected) => {
                  const selections = selected
                    ? state.policies.data.reduce((acc: string[], row) => {
                        const selectedOnListScreen =
                          state.policies.selections.includes(
                            row.universalPolicyId
                          );

                        if (selectedOnListScreen) {
                          acc.push(row.universalPolicyId);
                        }
                        return acc;
                      }, [])
                    : [];
                  setFieldValue(getFullFieldName('selectedIds'), selections);
                }}
              />
              {errors[prefix]?.selectedIds && (
                <div className={confirmationStyles.errorContainer}>
                  <FormInputErrorMessage message="At least one policy must be selected." />
                </div>
              )}
              <Footer hasSecondaryMenu={true}>
                <div className={styles.footerContainer}>
                  <Button
                    className={styles.backButton}
                    type="button"
                    variant="secondary"
                    data-testid="reattribution-confirmation-back"
                    onClick={() => {
                      history.push(
                        `/admin/partners/${currentPartner.id}/reattribution`
                      );
                    }}
                    disabled={isSubmitting}
                  >
                    Back
                  </Button>
                  <div className={confirmationStyles.footerError}>
                    {error && (
                      <Alert
                        message={
                          error.message ? error.message : 'An error occurred'
                        }
                        data-testid="admin-footer-error-message"
                      />
                    )}
                  </div>
                  <Button
                    type="submit"
                    data-testid="reattribution-confirmation-submit"
                    onClick={() => {
                      if (isValid) {
                        // open confirmation modal and handleSubmit on confirmation
                        setShowModal(true);
                      } else {
                        // Allow formik to process errors
                        handleSubmit();
                      }
                    }}
                    isSubmitting={isSubmitting}
                    disabled={!values[prefix].selectedIds.length}
                  >
                    Submit Reattribution ({values[prefix].selectedIds.length})
                  </Button>
                </div>
              </Footer>
            </>
          );
        }}
      </Formik>
    </AnimatedScreen>
  );
};
